import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "@/store/shared/supabase";
import { dispatchDataError, dispatchNotice, describeSupabaseError } from "@/store/shared/dataErrors";
import { useSession } from "@/session/SessionProvider";
import { createUuid } from "@/store/shared/id";
import { fullAccessMap, hasPermission } from "@/store/permissions/catalog";
import type { PermissionMap } from "@/store/permissions/catalog";
import {
  type Employe,
  type EmployeInput,
  type EmployeStatut,
} from "./types";

interface EmployesContextValue {
  employes: Employe[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  /** Employé dont les permissions sont appliquées à l'interface. `null` = accès complet (DG). */
  currentEmployeId: string | null;
  currentEmploye: Employe | null;
  isFullAccess: boolean;
  permissions: PermissionMap;
  can: (permission: string) => boolean;
  addEmploye: (input: EmployeInput) => Employe;
  /** Renvoie l'invitation d'un employé qui n'a pas encore activé son compte. */
  resendInvitation: (id: string) => void;
  updateEmploye: (id: string, input: EmployeInput) => void;
  removeEmploye: (id: string) => void;
  updateStatut: (id: string, statut: EmployeStatut) => void;
  updatePermissions: (id: string, permissions: PermissionMap) => void;
  getEmploye: (id: string) => Employe | undefined;
  setCurrentEmployeId: (id: string | null) => void;
}

const EmployesContext = createContext<EmployesContextValue | null>(null);

type Row = Record<string, unknown>;
const str = (value: unknown): string => (typeof value === "string" ? value : "");

function toPermissionMap(value: unknown): PermissionMap {
  if (value && typeof value === "object" && !Array.isArray(value)) return value as PermissionMap;
  return {};
}

function rowToEmploye(row: Row): Employe {
  return {
    id: str(row.id),
    prenom: str(row.prenom),
    nom: str(row.nom),
    email: str(row.email),
    telephone: str(row.telephone),
    // Le « rôle » métier est persisté dans la colonne `fonction` (aucun droit
    // système : le compte reste toujours `employe_agence`).
    role: str(row.fonction),
    statut: (str(row.statut) || "actif") as EmployeStatut,
    permissions: toPermissionMap(row.permissions),
    createdAt: str(row.created_at),
  };
}

/**
 * Employés de l'organisation (agence OU propriétaire) — branché sur Supabase.
 * - Lecture : table `employes_agence` (RLS = entreprise du compte authentifié).
 * - Écriture : Edge Functions sécurisées (création d'un compte Auth = service role).
 * Le rattachement est toujours imposé côté serveur à partir de la session.
 */
export function EmployesProvider({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const [employes, setEmployes] = useState<Employe[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentEmployeId, setCurrentEmployeId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from("employes_agence")
        .select("*")
        .order("created_at", { ascending: false });
      if (fetchError) throw fetchError;
      setEmployes(((data as Row[] | null) ?? []).map(rowToEmploye));
    } catch (caught) {
      setError(describeSupabaseError(caught));
      console.error("[Employés] chargement Supabase échoué", caught);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (
      !session ||
      (session.role !== "dg_agence" && session.role !== "employe_agence" && session.role !== "dg_proprietaire")
    ) {
      setEmployes([]);
      return;
    }
    void load();
  }, [session, load]);

  const invokeEmploye = useCallback(async (slug: string, payload: Record<string, unknown>) => {
    const { data, error: invokeError } = await supabase.functions.invoke(slug, { body: payload });
    if (invokeError) {
      // Une Edge Function en erreur (quota, doublon, validation…) répond en
      // JSON. `FunctionsHttpError` expose ce corps dans `context` : on le lit
      // pour remonter le VRAI motif au lieu du seul code HTTP générique.
      let serverMessage = "";
      const context = (invokeError as { context?: Response }).context;
      if (context && typeof context.clone === "function") {
        try {
          const parsed = (await context.clone().json()) as { error?: string; detail?: string } | null;
          serverMessage = [parsed?.error, parsed?.detail].filter(Boolean).join(" — ");
        } catch {
          try {
            const text = await context.clone().text();
            if (text) serverMessage = text;
          } catch {
            /* corps illisible : on garde le message générique */
          }
        }
      }
      throw new Error(serverMessage || invokeError.message || "Erreur serveur");
    }
    const result = data as { ok?: boolean; error?: string; detail?: string } | null;
    if (result && result.error) throw new Error(result.detail ? `${result.error} — ${result.detail}` : result.error);
    return data;
  }, []);

  const addEmploye = useCallback(
    (input: EmployeInput) => {
      const optimistic: Employe = { ...input, id: createUuid(), createdAt: new Date().toISOString() };
      setEmployes((prev) => [optimistic, ...prev]);
      void (async () => {
        try {
          await invokeEmploye("creer-employe", {
            prenom: input.prenom,
            nom: input.nom,
            email: input.email,
            telephone: input.telephone,
            fonction: input.role,
            permissions: input.permissions,
          });
          void load();
          dispatchNotice({
            scope: "Employés",
            tone: "success",
            message: `Invitation envoyée à ${input.email}. L'employé crée son mot de passe via le lien reçu.`,
          });
        } catch (caught) {
          setEmployes((prev) => prev.filter((item) => item.id !== optimistic.id));
          const reason = describeSupabaseError(caught);
          dispatchDataError({
            scope: "Employés",
            message: `Impossible de créer l'employé.${reason ? ` ${reason}` : ""}`,
            technical: reason,
          });
        }
      })();
      return optimistic;
    },
    [invokeEmploye, load]
  );

  const resendInvitation = useCallback(
    (id: string) => {
      const employe = employes.find((item) => item.id === id);
      void (async () => {
        try {
          await invokeEmploye("inviter-employe", { employeId: id });
          void load();
          dispatchNotice({
            scope: "Employés",
            tone: "success",
            message: employe?.email
              ? `Invitation renvoyée à ${employe.email}.`
              : "Invitation renvoyée à l'employé.",
          });
        } catch (caught) {
          const reason = describeSupabaseError(caught);
          dispatchDataError({
            scope: "Employés",
            message: `Impossible de renvoyer l'invitation.${reason ? ` ${reason}` : ""}`,
            technical: reason,
          });
        }
      })();
    },
    [employes, invokeEmploye, load]
  );

  const updateEmploye = useCallback(
    (id: string, input: EmployeInput) => {
      const previous = employes.find((item) => item.id === id);
      setEmployes((prev) => prev.map((item) => (item.id === id ? { ...item, ...input } : item)));
      void (async () => {
        try {
          await invokeEmploye("modifier-employe", {
            employeId: id,
            prenom: input.prenom,
            nom: input.nom,
            telephone: input.telephone,
            fonction: input.role,
            statut: input.statut,
            permissions: input.permissions,
          });
          void load();
        } catch (caught) {
          if (previous) setEmployes((prev) => prev.map((item) => (item.id === id ? previous : item)));
          const reason = describeSupabaseError(caught);
          dispatchDataError({
            scope: "Employés",
            message: `Impossible de modifier l'employé.${reason ? ` ${reason}` : ""}`,
            technical: reason,
          });
        }
      })();
    },
    [employes, invokeEmploye, load]
  );

  const removeEmploye = useCallback(
    (id: string) => {
      const previous = employes.find((item) => item.id === id);
      setEmployes((prev) => prev.filter((item) => item.id !== id));
      setCurrentEmployeId((current) => (current === id ? null : current));
      void (async () => {
        try {
          await invokeEmploye("supprimer-employe", { employeId: id });
          void load();
        } catch (caught) {
          if (previous) setEmployes((prev) => [previous, ...prev]);
          const reason = describeSupabaseError(caught);
          dispatchDataError({
            scope: "Employés",
            message: `Impossible de supprimer l'employé.${reason ? ` ${reason}` : ""}`,
            technical: reason,
          });
        }
      })();
    },
    [employes, invokeEmploye, load]
  );

  const updateStatut = useCallback(
    (id: string, statut: EmployeStatut) => {
      const employe = employes.find((item) => item.id === id);
      if (!employe) return;
      setEmployes((prev) => prev.map((item) => (item.id === id ? { ...item, statut } : item)));
      void (async () => {
        try {
          await invokeEmploye("modifier-employe", {
            employeId: id,
            prenom: employe.prenom,
            nom: employe.nom,
            telephone: employe.telephone,
            fonction: employe.role,
            statut,
            permissions: employe.permissions,
          });
          void load();
        } catch (caught) {
          setEmployes((prev) => prev.map((item) => (item.id === id ? { ...item, statut: employe.statut } : item)));
          const reason = describeSupabaseError(caught);
          dispatchDataError({
            scope: "Employés",
            message: `Impossible de changer le statut de l'employé.${reason ? ` ${reason}` : ""}`,
            technical: reason,
          });
        }
      })();
    },
    [employes, invokeEmploye, load]
  );

  const updatePermissions = useCallback(
    (id: string, permissions: PermissionMap) => {
      const employe = employes.find((item) => item.id === id);
      if (!employe) return;
      setEmployes((prev) => prev.map((item) => (item.id === id ? { ...item, permissions } : item)));
      void (async () => {
        try {
          await invokeEmploye("modifier-employe", {
            employeId: id,
            prenom: employe.prenom,
            nom: employe.nom,
            telephone: employe.telephone,
            fonction: employe.role,
            statut: employe.statut,
            permissions,
          });
          void load();
        } catch (caught) {
          setEmployes((prev) =>
            prev.map((item) => (item.id === id ? { ...item, permissions: employe.permissions } : item))
          );
          const reason = describeSupabaseError(caught);
          dispatchDataError({
            scope: "Employés",
            message: `Impossible de modifier les permissions.${reason ? ` ${reason}` : ""}`,
            technical: reason,
          });
        }
      })();
    },
    [employes, invokeEmploye, load]
  );

  const getEmploye = useCallback((id: string) => employes.find((item) => item.id === id), [employes]);

  const currentEmploye = useMemo(
    () => employes.find((item) => item.id === currentEmployeId) ?? null,
    [employes, currentEmployeId]
  );

  // Un employé d'agence connecté ne doit JAMAIS disposer de l'accès complet :
  // ses permissions réelles proviennent du serveur (session.permissions, issue
  // de la RPC `mon_profil`). Pour un DG, l'accès complet reste la règle, sauf
  // lorsqu'il « prévisualise » les permissions d'un employé de son organisation.
  const isEmploye = session?.role === "employe_agence";

  const permissions = useMemo<PermissionMap>(() => {
    if (isEmploye) return session?.permissions ?? {};
    if (currentEmploye) return currentEmploye.permissions;
    return fullAccessMap();
  }, [isEmploye, session?.permissions, currentEmploye]);

  const isFullAccess = !isEmploye && currentEmploye === null;

  const can = useCallback(
    (permission: string) => {
      if (!isEmploye && !currentEmploye) return true;
      return hasPermission(permissions, permission);
    },
    [isEmploye, currentEmploye, permissions]
  );

  const value = useMemo(
    () => ({
      employes,
      loading,
      error,
      reload: load,
      currentEmployeId,
      currentEmploye,
      isFullAccess,
      permissions,
      can,
      addEmploye,
      resendInvitation,
      updateEmploye,
      removeEmploye,
      updateStatut,
      updatePermissions,
      getEmploye,
      setCurrentEmployeId,
    }),
    [
      employes,
      loading,
      error,
      load,
      currentEmployeId,
      currentEmploye,
      isFullAccess,
      permissions,
      can,
      addEmploye,
      resendInvitation,
      updateEmploye,
      removeEmploye,
      updateStatut,
      updatePermissions,
      getEmploye,
    ]
  );

  return <EmployesContext.Provider value={value}>{children}</EmployesContext.Provider>;
}

export function useEmployes(): EmployesContextValue {
  const context = useContext(EmployesContext);
  if (!context) {
    throw new Error("useEmployes doit être utilisé à l'intérieur de EmployesProvider");
  }
  return context;
}